import { Extent } from 'ol/extent';
import { Geometry } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';

import { makeAPICall } from 'models/api';
import { findConfig } from 'models/layer';
import { API_URL, GEOSERVER_URL, MAP_PROJECTION } from 'config';
import { BfWorkflowId, FaftWorkflowId } from 'models/workflow';

export declare namespace API {
    export interface Extent {
        xmin: number;
        ymin: number;
        xmax: number;
        ymax: number;
    }

    export interface BFMCGroup {
        id: number;
        name: string;
        bmcs: API.BFMC['id'][];
    }

    export interface StatusUpdate {
        status_old: BfWorkflowId | FaftWorkflowId | null;
        status_new: BfWorkflowId | FaftWorkflowId | null;
        created: string;
        modified_by: string;
    }

    export interface Plan {
        id: string;
        plan_type: PlanType;
        description: string;
        name: string;
        code: string;
        workshop_status: BfWorkflowId | FaftWorkflowId | 'null';
        plan_status: 'Draft' | 'Public' | 'Proposed' | 'Retired' | 'Unknown';
        end_date: string;
        updates: StatusUpdate[];
    }

    export interface BFMC {
        id: number;
        name: string;
        code: string;
        extent: Extent;
        plans: Plan[];
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }
}

export type PlanType = 'bfrmp' | 'faft';

export interface Plan {
    id: string;
    plan_type: PlanType;
    description: string;
    name: string;
    code: string;
    workshop_status: BfWorkflowId | FaftWorkflowId;
    plan_status: 'Draft' | 'Public' | 'Proposed' | 'Retired' | 'Unknown';
    end_date: string;
    updates: StatusUpdate[];
}

export interface BFMCGroup {
    id: number;
    name: string;
    bmcs: BFMC['id'][];
}

export interface StatusUpdate {
    from: BfWorkflowId | FaftWorkflowId | null;
    to: BfWorkflowId | FaftWorkflowId | null;
    date: Date;
    user: string;
}

export interface BFMCMin {
    id: number;
    name: string;
    code: string;
    plans: Plan[];
}

export interface BFMC {
    id: number;
    name: string;
    code: string;
    extent: Extent;
    plans: Plan[];
    faft_plan_id: string;
    bfrmp_plan_id: string;
}

const BFMC_ROUTE = `${API_URL}/bmcs`;
const BFMC_GROUPS_ROUTE = `${API_URL}/bmc-groups`;

export const mapAPIToStatusUpdate = ({
    created,
    modified_by,
    status_new,
    status_old,
}: API.StatusUpdate): StatusUpdate => ({
    from: status_old,
    to: status_new,
    user: modified_by,
    date: new Date(created),
});

export const mapAPIToPlan = ({
    id,
    plan_type,
    description,
    code,
    name,
    workshop_status,
    plan_status,
    end_date,
    updates,
}: API.Plan): Plan => ({
    id: id,
    plan_type,
    description,
    code,
    name,
    // remove when not nulls in database
    workshop_status:
        workshop_status === 'null'
            ? plan_type === 'bfrmp'
                ? 'bf_not_started'
                : 'faft_not_started'
            : workshop_status,
    plan_status,
    end_date,
    updates: updates.map(mapAPIToStatusUpdate),
});

const getPlanIdFromPlans = (plans: Plan[], plan_type: PlanType) => {
    const matchingPlan = plans.find((plan) => plan.plan_type === plan_type);
    if (!matchingPlan) {
        return 'NA';
    }
    return matchingPlan.id;
};

export const mapAPIToBFMC = ({ plans, extent, ...rest }: API.BFMC): BFMC => ({
    ...rest,
    plans: plans.map(mapAPIToPlan),
    extent: [extent.xmin, extent.ymin, extent.xmax, extent.ymax],
    faft_plan_id: getPlanIdFromPlans(plans.map(mapAPIToPlan) || [], 'faft'),
    bfrmp_plan_id: getPlanIdFromPlans(plans.map(mapAPIToPlan) || [], 'bfrmp'),
});

export const mapAPIToBFMCGroup = ({ ...rest }: API.BFMCGroup): BFMCGroup => ({
    ...rest,
});

export const getBFMCs = makeAPICall<BFMC[], void, API.BFMC[]>(
    () => ({
        url: `${BFMC_ROUTE}/?limit=1000`,
    }),
    (raw) => raw.map(mapAPIToBFMC).sort((a, b) => a.name.localeCompare(b.name)),
);

export const getBFMCGroups = makeAPICall<BFMCGroup[], void, API.BFMCGroup[]>(
    () => ({
        url: `${BFMC_GROUPS_ROUTE}/`,
    }),
    (raw) => raw.map(mapAPIToBFMCGroup),
);

export const getBFMCGeometry = makeAPICall<
    Geometry | null,
    { bfmc: BFMC },
    any
>(
    ({ bfmc }) => {
        const serviceName = findConfig('Admin_BMC')?.serviceName ?? '';
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: serviceName,
            outputFormat: 'application/json',
            srsname: MAP_PROJECTION.getCode(),
            CQL_FILTER: `BMC='${bfmc.name}'`,
        });

        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },
    (raw) => new GeoJSON().readFeatures(raw)?.[0].getGeometry() ?? null,
);

export const updateStatus = makeAPICall<
    API.ActionResponse,
    {
        bfmcs: BFMC['id'][];
        plan_type: PlanType;
        status: BfWorkflowId | FaftWorkflowId;
    },
    API.ActionResponse
>(
    ({ bfmcs, plan_type, status }) => {
        const data = new FormData();

        data.append('bmc_ids', bfmcs.join(','));
        data.append('plan_type', plan_type);
        data.append('workshop_status', status);
        return {
            url: `${BFMC_ROUTE}/workshop`,
            contentType: null,
            requestData: {
                method: 'PUT',
                body: data,
            },
        };
    },
    (x) => x,
);

export const addPlan = makeAPICall<
    API.ActionResponse,
    { bfmc_id: BFMC['id']; plan_type: PlanType; retain_existing: boolean },
    API.ActionResponse
>(
    ({ bfmc_id, plan_type, retain_existing }) => {
        const data = new FormData();

        data.append('retain_existing', String(retain_existing));

        return {
            url: `${BFMC_ROUTE}/${bfmc_id}/plans/${plan_type}`,
            contentType: null,
            requestData: {
                method: 'POST',
                body: data,
            },
        };
    },
    (x) => x,
);

export const updatePlanDetails = makeAPICall<
    API.ActionResponse,
    {
        bfmc_id: BFMC['id'];
        plan_id: Plan['id'];
        code: Plan['code'];
        name: Plan['name'];
    },
    API.ActionResponse
>(
    ({ bfmc_id, plan_id, code, name }) => {
        return {
            url: `${BFMC_ROUTE}/${bfmc_id}/${plan_id}`,
            contentType: 'application/json',
            requestData: {
                method: 'PUT',
                body: JSON.stringify({
                    code: code,
                    name: name,
                }),
            },
        };
    },
    (x) => x,
);

export const sendDataToServiceNow = makeAPICall<
    API.ActionResponse,
    {
        bfmc_id: BFMC['id'];
        plan_id: Plan['id'];
        plan_type: Plan['plan_type'];
    },
    API.ActionResponse
>(
    ({ bfmc_id, plan_id, plan_type }) => {
        return {
            url: `${BFMC_ROUTE}/send-data/servicenow`,
            contentType: 'application/json',
            requestData: {
                method: 'POST',
                body: JSON.stringify({
                    bfmc_id: bfmc_id,
                    plan_id: plan_id,
                    plan_type: plan_type,
                }),
            },
        };
    },
    (x) => x,
);

export const getBFMCGroupFromBFMC = (
    bfmc: BFMC,
    groups?: BFMCGroup[] | null,
): BFMCGroup | null =>
    groups?.find((group) => group.bmcs.includes(bfmc.id)) ?? null;

export const getBFMCGroupFromBFMCId = (
    bfmcId: number,
    groups: BFMCGroup[] | null,
): BFMCGroup | null =>
    groups?.find((group) => group.bmcs.includes(bfmcId)) ?? null;
